/**
 * Settings → Scrapers (PRD "Settings popup", §5.1 #35-#38, §12.5).
 *
 * Two active pickers bind a plugin to each data kind, and one card per installed plugin carries its
 * config form. The form is **schema-driven**: the fields come from the plugin's own descriptors, so
 * a newly added plugin is configurable without touching this file — which is the whole point of the
 * plugin rework.
 *
 * The form itself is the shared `ConfigFieldsGrid` (the Comment Generation tab renders its AI
 * backends through the same one), which carries the write-only-secret rule. The rule this file
 * owns: an unconfigured plugin cannot be picked — its option is disabled and the card says why.
 */

import { useEffect, useState } from 'react';
import type { ScraperDTO, ScraperKind, ScrapersResponse, UnmappedFieldsDTO } from '@lw/shared';
import { SCRAPER_KINDS } from '@lw/shared';
import { describeError } from '../../api/client';
import { ErrorState, Spinner } from '../../components';
import { SCRAPER_KIND_LABEL, STAGE_ERROR_CLASS_LABEL } from '../../lib/labels';
import { pluralize } from '../../lib/format';
import { ConfigFieldsGrid, configFromDraft, fieldErrorsOf, type ConfigDraft } from './configFields';
import {
  useScrapersQuery,
  useSetActiveScraper,
  useTestScraper,
  useUpdateScraperConfig,
} from './useScrapers';

export type ScrapersSectionProps = {
  /** Payload counts for the "removed scraper" notice (#18); `null` when there are none. */
  unmappable: { count: number; pluginIds: string[] } | null;
  /**
   * Per-plugin "posts carry fields the mapper does not read" aggregate (#18). Informational, same
   * tone as `unmappable`: it is the signal that a mapper update is due, not an error.
   */
  unmappedFields: UnmappedFieldsDTO[];
};

export function ScrapersSection({ unmappable, unmappedFields }: ScrapersSectionProps) {
  const query = useScrapersQuery();
  const data = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading scrapers…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load scrapers"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (data === undefined) return null;

  return (
    <>
      <ActivePickers data={data} />
      {unmappable !== null ? (
        <p className="settings-warn" role="status">
          {pluralize(unmappable.count, 'stored payload')} reference removed{' '}
          {unmappable.pluginIds.length === 1 ? 'scraper' : 'scrapers'}{' '}
          {unmappable.pluginIds.map((id) => `\`${id}\``).join(', ')}. They are kept on disk and
          become mappable again if that plugin is reinstalled.
        </p>
      ) : null}
      {unmappedFields.map((entry) => (
        <p className="settings-note" role="status" key={entry.pluginId}>
          {/* Both strings are built server-side, truncation included (§12.5). */}
          {entry.postsDisplay} carry fields <strong>{entry.pluginName}</strong> does not map yet (
          {entry.keysDisplay}). Nothing is lost — the values are stored and visible under Raw data
          in a post preview.
        </p>
      ))}
      {data.scrapers.map((scraper) => (
        <ScraperCard key={scraper.id} scraper={scraper} />
      ))}
      {data.scrapers.length === 0 ? (
        <p className="muted">
          No scraper plugins are installed. Add one under <code>packages/server/src/plugins/</code>{' '}
          and rebuild — see <code>SCRAPER_PLUGINS_README.md</code>.
        </p>
      ) : null}
    </>
  );
}

function ActivePickers({ data }: { data: ScrapersResponse }) {
  const setActive = useSetActiveScraper();

  return (
    <section className="settings-section">
      <h3 className="settings-title">Active scrapers</h3>
      <p className="field__hint">
        One plugin per data kind. Changes take effect on the next run; picking None disables that
        kind entirely.
      </p>
      <div className="settings-grid">
        {SCRAPER_KINDS.map((kind: ScraperKind) => {
          const options = data.scrapers.filter((scraper) => scraper.kind === kind);
          return (
            <div className="field" key={kind}>
              <label className="field__label" htmlFor={`settings-active-${kind}`}>
                {SCRAPER_KIND_LABEL[kind]}
              </label>
              <select
                id={`settings-active-${kind}`}
                className="select"
                value={data.active[kind] ?? ''}
                disabled={setActive.isPending}
                onChange={(event) => {
                  setActive.mutate({
                    kind,
                    pluginId: event.target.value === '' ? null : event.target.value,
                  });
                }}
              >
                <option value="">None (disabled)</option>
                {options.map((scraper) => (
                  <option key={scraper.id} value={scraper.id} disabled={!scraper.configured}>
                    {scraper.name}
                    {scraper.configured ? '' : ' — not configured'}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      {setActive.isError ? (
        <p className="form-error" role="alert">
          {describeError(setActive.error)}
        </p>
      ) : null}
    </section>
  );
}

function ScraperCard({ scraper }: { scraper: ScraperDTO }) {
  const save = useUpdateScraperConfig();
  const test = useTestScraper();
  const [draft, setDraft] = useState<ConfigDraft>({});

  // A save answers with the fresh payload; drop the draft so the inputs show the stored truth.
  useEffect(() => {
    setDraft({});
  }, [scraper]);

  const dirty = Object.keys(draft).length > 0;

  const submit = () => {
    if (!dirty) return;
    save.mutate({ pluginId: scraper.id, config: configFromDraft(scraper.fields, draft) });
  };

  const fieldErrors = fieldErrorsOf(save.error);

  return (
    <section className="settings-section">
      <div className="scraper-head">
        <h3 className="settings-title">{scraper.name}</h3>
        <span className="tag">{SCRAPER_KIND_LABEL[scraper.kind]}</span>
        {scraper.active ? (
          <span className="status status--ok">
            <span className="status__dot" aria-hidden="true" />
            Active
          </span>
        ) : null}
        {scraper.configured ? null : (
          <span className="status status--queued">
            <span className="status__dot" aria-hidden="true" />
            Not configured
          </span>
        )}
        <span className="faint scraper-meta">
          <code>{scraper.id}</code> · map v{scraper.version}
        </span>
      </div>
      <p className="muted">{scraper.description}</p>

      <ConfigFieldsGrid
        idPrefix={`settings-cfg-${scraper.id}`}
        fields={scraper.fields}
        draft={draft}
        onChange={(key, value) => setDraft({ ...draft, [key]: value })}
        errors={fieldErrors}
        onClearSecret={(key) => save.mutate({ pluginId: scraper.id, config: { [key]: '' } })}
        busy={save.isPending}
      />

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={submit}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? <Spinner size="sm" /> : null}
          Save
        </button>

        {scraper.testable ? (
          <button
            type="button"
            className="table-action"
            onClick={() => test.mutate(scraper.id)}
            disabled={test.isPending || !scraper.configured}
          >
            {test.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Test connection</span>
          </button>
        ) : null}

        {test.isSuccess ? (
          test.data.ok ? (
            <span className="settings-ok">{test.data.info ?? 'Connected.'}</span>
          ) : (
            <span className="settings-bad">
              {STAGE_ERROR_CLASS_LABEL[test.data.errorClass as keyof typeof STAGE_ERROR_CLASS_LABEL] ??
                test.data.errorClass}
              : {test.data.message}
            </span>
          )
        ) : null}
        {test.isError ? <span className="settings-bad">{describeError(test.error)}</span> : null}
      </div>

      {save.isError && Object.keys(fieldErrors).length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(save.error)}
        </p>
      ) : null}
    </section>
  );
}

export default ScrapersSection;
